import React, { useEffect, useMemo, useState } from 'react';
import api from '../api/api';
import { Alert, ErrorState, PageSkeleton } from '../components';
import { PageHeader } from '../components/layout';
import ConnectionTestPanel from '../components/ai-settings/ConnectionTestPanel';

const SUPPORTED_PROVIDERS = [
  { provider: 'openai', label: 'OpenAI' },
  { provider: 'anthropic', label: 'Anthropic Compatible' }
];

const newAgentConfig = () => ({
  id: `new-${Date.now()}`,
  name: '默认 Agent',
  base_url: 'https://codex-gateway.10vision.top/',
  api_key: '',
  project_path: '',
  provider: 'codex',
  agent_type: 'codex',
  model: '',
  workspace_id: 'jira',
  system_prompt: '',
  is_active: false,
  has_api_key: false,
  is_new: true
});

const ProviderIcon = ({ provider }) => (
  <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {provider === 'openai'
      ? <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
      : <><circle cx="12" cy="12" r="10" /><path d="M12 8v4l3 3" /></>}
  </svg>
);

const AISettingsPage = ({ canWrite = false }) => {
  const [configs, setConfigs] = useState([]);
  const [agentConfigs, setAgentConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState({});
  const [alertState, setAlertState] = useState({ isOpen: false, message: '' });
  const [activeTab, setActiveTab] = useState('providers');
  const [selectedTestTarget, setSelectedTestTarget] = useState('provider-openai');

  useEffect(() => { fetchAIConfigs(); }, []);

  const fetchAIConfigs = async () => {
    try {
      setLoading(true);
      setError(null);
      const [response, agentResponse] = await Promise.all([api.getAIConfigs(), api.getAgentConfigs()]);
      const serverConfigs = Array.isArray(response.data) ? response.data : [];
      const serverAgentConfigs = Array.isArray(agentResponse.data) ? agentResponse.data : [];
      setConfigs(SUPPORTED_PROVIDERS.map(({ provider }) => serverConfigs.find(item => item.provider === provider) || ({
        id: provider, provider, api_key: '', model: '', is_active: false, base_url: '', has_api_key: false
      })));
      setAgentConfigs(serverAgentConfigs.map(item => ({ ...item, agent_type: item.agent_type || 'cloudcli', api_key: '', is_new: false })));
    } catch (requestError) {
      setError(requestError.response?.data?.detail || requestError.message);
    } finally {
      setLoading(false);
    }
  };

  const updateConfig = (provider, field, value) => setConfigs(current => current.map(config => (
    config.provider === provider ? { ...config, [field]: value } : config
  )));

  const updateAgentConfigField = (id, field, value) => setAgentConfigs(current => current.map(config => (
    config.id === id ? { ...config, [field]: value } : config
  )));

  const saveConfig = async (provider) => {
    setSaving(current => ({ ...current, [provider]: true }));
    try {
      const config = configs.find(item => item.provider === provider);
      const payload = { model: config.model, is_active: config.is_active, base_url: config.base_url };
      if (config.api_key) payload.api_key = config.api_key;
      await api.updateAIConfig(provider, payload);
      setConfigs(current => current.map(item => item.provider === provider
        ? { ...item, api_key: '', has_api_key: item.has_api_key || Boolean(config.api_key) }
        : item));
      setAlertState({ isOpen: true, message: '配置保存成功！' });
    } catch (requestError) {
      const message = requestError.response?.data?.detail || requestError.message;
      setError(message);
      setAlertState({ isOpen: true, message: `保存失败：${message}` });
    } finally {
      setSaving(current => ({ ...current, [provider]: false }));
    }
  };

  const saveAgentConfig = async (id) => {
    setSaving(current => ({ ...current, [`agent-${id}`]: true }));
    try {
      const config = agentConfigs.find(item => item.id === id);
      const payload = {
        name: config.name, base_url: config.base_url, project_path: config.project_path,
        provider: config.provider, agent_type: config.agent_type, model: config.model,
        workspace_id: config.workspace_id, system_prompt: config.system_prompt,
        is_active: config.is_active
      };
      if (config.api_key) payload.api_key = config.api_key;
      if (config.is_new) {
        if (!payload.api_key) throw new Error(config.agent_type === 'codex' ? '新增 Codex Gateway Agent 需要填写 HMAC Secret' : '新增 Agent 需要填写 API Key');
        const response = await api.createAgentConfig(payload);
        setAgentConfigs(current => current.map(item => item.id === id ? { ...response.data, api_key: '', is_new: false } : item));
      } else {
        const response = await api.updateAgentConfig(id, payload);
        setAgentConfigs(current => current.map(item => item.id === id ? { ...response.data, api_key: '', is_new: false } : item));
      }
      setAlertState({ isOpen: true, message: 'Agent 配置保存成功！' });
    } catch (requestError) {
      const message = requestError.response?.data?.detail || requestError.message;
      setAlertState({ isOpen: true, message: `保存失败：${message}` });
    } finally {
      setSaving(current => ({ ...current, [`agent-${id}`]: false }));
    }
  };

  const removeAgentConfig = async (id) => {
    const config = agentConfigs.find(item => item.id === id);
    if (!config) return;
    if (config.is_new) {
      setAgentConfigs(current => current.filter(item => item.id !== id));
      return;
    }
    setSaving(current => ({ ...current, [`agent-${id}`]: true }));
    try {
      await api.deleteAgentConfig(id);
      setAgentConfigs(current => current.filter(item => item.id !== id));
      setAlertState({ isOpen: true, message: 'Agent 配置已删除。' });
    } catch (requestError) {
      const message = requestError.response?.data?.detail || requestError.message;
      setAlertState({ isOpen: true, message: `删除失败：${message}` });
    } finally {
      setSaving(current => ({ ...current, [`agent-${id}`]: false }));
    }
  };

  const providerTargets = useMemo(() => configs.map(config => ({
    key: `provider-${config.provider}`,
    type: 'provider',
    targetId: config.provider,
    label: SUPPORTED_PROVIDERS.find(item => item.provider === config.provider)?.label || config.provider,
    apiKey: config.api_key,
    draft: { base_url: config.base_url, model: config.model }
  })), [configs]);

  const agentTargets = useMemo(() => agentConfigs.filter(config => config.is_active && !config.is_new).map(config => ({
    key: `agent-${config.id}`,
    type: 'agent',
    targetId: config.id,
    label: config.name || `Agent #${config.id}`,
    apiKey: config.api_key,
    draft: {
      base_url: config.base_url, model: config.model, project_path: config.project_path,
      provider: config.provider, agent_type: config.agent_type, workspace_id: config.workspace_id,
      system_prompt: config.system_prompt
    }
  })), [agentConfigs]);

  const selectTestTarget = (key) => setSelectedTestTarget(key);
  const switchTab = (tab) => {
    setActiveTab(tab);
    setSelectedTestTarget(tab === 'providers' ? (providerTargets[0]?.key || 'provider-openai') : (agentTargets[0]?.key || ''));
  };

  const renderProviderCard = (config) => {
    const label = SUPPORTED_PROVIDERS.find(item => item.provider === config.provider)?.label || config.provider;
    const providerDescription = config.provider === 'openai' ? '使用 OpenAI Chat Completions 接口' : '兼容 Anthropic Messages 接口的服务';
    return (
      <section key={config.provider} className="configuration-settings-section ai-provider-card">
        <div className="ai-card-head">
          <div className="ai-card-title">
            <span className="ai-icon-box"><ProviderIcon provider={config.provider} /></span>
            <div><h2>{label}</h2><p>{providerDescription}</p></div>
          </div>
          <span className={`ai-status-pill ${config.is_active ? '' : 'is-muted'}`}>{config.is_active ? '已启用' : '未启用'}</span>
        </div>
        <div className="ai-card-body ai-provider-fields">
          <div className="ai-field ai-field-full">
            <label>API Key</label>
            <input type="password" value={config.api_key || ''} onChange={(event) => updateConfig(config.provider, 'api_key', event.target.value)} placeholder={config.has_api_key ? '已配置，留空表示保持不变' : '请输入 API Key'} disabled={!canWrite} />
            <small>密钥仅随保存或右侧本次测试请求发送。</small>
          </div>
          <div className="ai-field-grid">
            <div className="ai-field"><label>模型</label><input value={config.model || ''} onChange={(event) => updateConfig(config.provider, 'model', event.target.value)} placeholder="例如 gpt-5" disabled={!canWrite} /></div>
            <div className="ai-field"><label>基础 URL</label><input value={config.base_url || ''} onChange={(event) => updateConfig(config.provider, 'base_url', event.target.value)} placeholder="https://api.example.com/v1" disabled={!canWrite} /></div>
          </div>
          <div className="ai-card-actions">
            <label className="ai-enabled-toggle"><input type="checkbox" checked={Boolean(config.is_active)} onChange={(event) => updateConfig(config.provider, 'is_active', event.target.checked)} disabled={!canWrite} />启用此提供商</label>
            <div className="ai-row-buttons">
              <button className="btn btn-secondary" onClick={() => selectTestTarget(`provider-${config.provider}`)}>测试对话</button>
              {canWrite && <button className="btn btn-primary" onClick={() => saveConfig(config.provider)} disabled={saving[config.provider]}>{saving[config.provider] ? '保存中…' : '保存配置'}</button>}
            </div>
          </div>
        </div>
      </section>
    );
  };

  const renderAgentRow = (config) => {
    const isCodexGateway = config.agent_type === 'codex';
    const connectionLabel = isCodexGateway ? 'Codex Gateway' : 'CloudCLI';
    const locationLabel = isCodexGateway ? (config.workspace_id || '未设置 workspace') : (config.project_path || '未设置项目路径');
    return (
      <article key={config.id} className="ai-agent-row">
        <div className="ai-agent-row-heading">
          <div className="ai-agent-identity">
            <h3>{config.name || '未命名 Agent'}</h3>
            <p><span>{connectionLabel}</span> · {locationLabel} · {config.has_api_key ? '已配置密钥' : '未配置密钥'}</p>
          </div>
          <div className="ai-agent-row-actions">
            <span className={`ai-status-pill ${config.is_active ? '' : 'is-muted'}`}>{config.is_active ? '已启用' : '未启用'}</span>
            <button className="btn btn-secondary btn-sm" onClick={() => selectTestTarget(`agent-${config.id}`)} disabled={!config.is_active || config.is_new}>测试对话</button>
          </div>
        </div>
        <div className="ai-agent-fields">
          <div className="ai-field"><label>Agent 名称</label><input aria-label="Agent 名称" value={config.name || ''} onChange={(event) => updateAgentConfigField(config.id, 'name', event.target.value)} placeholder="例如 Jira Review Agent" disabled={!canWrite} /></div>
          <div className="ai-field"><label>接入类型</label><select value={config.agent_type || 'cloudcli'} onChange={(event) => updateAgentConfigField(config.id, 'agent_type', event.target.value)} disabled={!canWrite}><option value="cloudcli">CloudCLI</option><option value="codex">Codex Gateway</option></select></div>
          {isCodexGateway ? <div className="ai-field"><label>Workspace ID</label><input value={config.workspace_id || ''} onChange={(event) => updateAgentConfigField(config.id, 'workspace_id', event.target.value)} placeholder="jira" disabled={!canWrite} /></div> : <div className="ai-field"><label>Agent Provider</label><select value={config.provider || 'codex'} onChange={(event) => updateAgentConfigField(config.id, 'provider', event.target.value)} disabled={!canWrite}><option value="codex">codex</option><option value="claude">claude</option><option value="cursor">cursor</option></select></div>}
          {!isCodexGateway && <div className="ai-field"><label>模型</label><input value={config.model || ''} onChange={(event) => updateAgentConfigField(config.id, 'model', event.target.value)} placeholder="默认模型" disabled={!canWrite} /></div>}
          <div className="ai-field"><label>基础 URL</label><input value={config.base_url || ''} onChange={(event) => updateAgentConfigField(config.id, 'base_url', event.target.value)} placeholder={isCodexGateway ? 'https://codex-gateway.10vision.top/' : 'https://cloud.example.com'} disabled={!canWrite} /></div>
          <div className="ai-field"><label>{isCodexGateway ? 'Gateway HMAC Secret' : 'API Key'}</label><input type="password" value={config.api_key || ''} onChange={(event) => updateAgentConfigField(config.id, 'api_key', event.target.value)} placeholder={config.has_api_key ? '已配置，留空表示保持不变' : (isCodexGateway ? '请输入 Gateway HMAC Secret' : '请输入 X-API-Key')} disabled={!canWrite} /></div>
          {!isCodexGateway && <div className="ai-field"><label>项目路径</label><input value={config.project_path || ''} onChange={(event) => updateAgentConfigField(config.id, 'project_path', event.target.value)} placeholder="/project/path" disabled={!canWrite} /></div>}
        </div>
        <div className="ai-field ai-agent-system-prompt"><label>System Prompt</label><textarea rows={3} value={config.system_prompt || ''} onChange={(event) => updateAgentConfigField(config.id, 'system_prompt', event.target.value)} placeholder={isCodexGateway ? '可选。会与企业微信消息一起传给 Codex Gateway。' : '可选。留空时不会向 Agent 传 system prompt。'} disabled={!canWrite} /></div>
        <div className="ai-card-actions">
          <label className="ai-enabled-toggle"><input type="checkbox" checked={Boolean(config.is_active)} onChange={(event) => updateAgentConfigField(config.id, 'is_active', event.target.checked)} disabled={!canWrite} />启用此 Agent</label>
          {canWrite && <div className="ai-row-buttons"><button className="btn btn-secondary" onClick={() => removeAgentConfig(config.id)} disabled={saving[`agent-${config.id}`]}>删除</button><button className="btn btn-primary" onClick={() => saveAgentConfig(config.id)} disabled={saving[`agent-${config.id}`]}>{saving[`agent-${config.id}`] ? '保存中…' : '保存 Agent'}</button></div>}
        </div>
      </article>
    );
  };


  if (error && !loading) {
    return <div className="configuration-list-page ai-settings-page"><PageHeader title="AI 设置" /><ErrorState title="无法加载 AI 设置" description={error} onRetry={fetchAIConfigs} /></div>;
  }

  const activeProviderCount = configs.filter(config => config.is_active).length;
  const activeTarget = (activeTab === 'providers' ? providerTargets : agentTargets).find(target => target.key === selectedTestTarget);

  return (
    <div className="configuration-list-page ai-settings-page">
      <PageHeader title="AI 设置" />

      {loading ? <PageSkeleton metricCount={3} rowCount={5} label="加载 AI 设置中" /> : <>
        <section className="ai-settings-overview" aria-label="AI 配置概览">
          <div className="ai-overview-metric"><div className="ai-overview-label"><i />AI 提供商</div><strong>{activeProviderCount} / {SUPPORTED_PROVIDERS.length} 已启用</strong><span>OpenAI · Anthropic Compatible</span></div>
          <div className="ai-overview-metric"><div className="ai-overview-label"><i />Agent 配置</div><strong>{agentConfigs.length} 个 Agent</strong><span>按 project path 区分运行环境</span></div>
          <div className="ai-overview-metric"><div className="ai-overview-label"><i />当前测试目标</div><strong>{activeTarget?.label || '未选择目标'}</strong><span>也可切换到任意已启用 Agent</span></div>
        </section>

        <div className="admin-page-tabs" role="tablist" aria-label="AI 设置分类">
          <button role="tab" aria-selected={activeTab === 'providers'} onClick={() => switchTab('providers')}>AI 提供商</button>
          <button role="tab" aria-selected={activeTab === 'agents'} onClick={() => switchTab('agents')}>Agent 提供商 <span>{agentConfigs.length}</span></button>
        </div>

        <div className="ai-settings-workspace" role="tabpanel">
          <div className="ai-settings-editor">
            {activeTab === 'providers' ? <div className="ai-provider-stack">{configs.map(renderProviderCard)}</div> : <section className="configuration-settings-section ai-agent-list-section ai-agent-card"><div className="ai-card-head ai-agent-list-header"><div className="ai-card-title"><span className="ai-icon-box">⌁</span><div><h2>Agent 提供商</h2><p>支持 CloudCLI 项目路径与 Codex Gateway workspace 两种接入</p></div></div>{canWrite && <button className="btn btn-primary" onClick={() => setAgentConfigs(current => [...current, newAgentConfig()])}>＋ 新增 Agent</button>}</div><div className="ai-agent-list-body">{agentConfigs.length ? agentConfigs.map(renderAgentRow) : <div className="ai-agent-empty">尚未添加 Agent。新增后可保存并启用，然后在右侧进行对话测试。</div>}</div></section>}
          </div>
          <ConnectionTestPanel targets={activeTab === 'providers' ? providerTargets : agentTargets} canWrite={canWrite} selectedTargetKey={selectedTestTarget} onTargetChange={setSelectedTestTarget} />
        </div>
      </>}
      <Alert isOpen={alertState.isOpen} title="提示" message={alertState.message} onClose={() => setAlertState(current => ({ ...current, isOpen: false }))} />
    </div>
  );
};

export default AISettingsPage;
